/**
 * InspectionStepGrid - Step 2: Matrix of vehicles × resource items
 *
 * Tab bar for groups, cells with 4-level buttons, odometer input, shortcuts.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  RESOURCE_COLUMNS,
  ALL_GROUPS,
  GROUP_LABELS,
  RANGE_GLYPHS,
  RANGE_COLORS,
  columnApplies,
  getGroupColumns,
} from '../../../../config/inspection.js';
import { useFormat } from '../../../../i18n/format.js';

const TABS = [
  ...ALL_GROUPS.map(g => ({ key: g, label: GROUP_LABELS[g] })),
  { key: 'all', label: 'Tout' },
];

export default function InspectionStepGrid({
  vehicles,
  readings,
  onReadingChange,
  odometers,
  onOdometerChange,
  setReadings,
}) {
  const { fmt } = useFormat();
  const [currentTab, setCurrentTab] = useState('tires');
  const focusRowRef = useRef(0);
  const focusColRef = useRef(0);
  const gridRef = useRef(null);

  // Columns for current tab
  const visibleColumns = currentTab === 'all'
    ? RESOURCE_COLUMNS
    : getGroupColumns(currentTab);

  // ---- Shortcuts ----
  const handleSetAllGreen = () => {
    const newReadings = { ...readings };
    for (const v of vehicles) {
      const vr = { ...(newReadings[v.id] || {}) };
      for (const col of visibleColumns) {
        if (columnApplies(col, v)) {
          vr[col.key] = 3; // ✓ good
        }
      }
      newReadings[v.id] = vr;
    }
    setReadings(newReadings);
  };

  const handleCopyFirstRow = () => {
    if (vehicles.length < 2) return;
    const firstVr = readings[vehicles[0].id] || {};
    const newReadings = { ...readings };
    for (let i = 1; i < vehicles.length; i++) {
      const vr = { ...(newReadings[vehicles[i].id] || {}) };
      for (const col of visibleColumns) {
        if (columnApplies(col, vehicles[i]) && firstVr[col.key] !== undefined) {
          vr[col.key] = firstVr[col.key];
        }
      }
      newReadings[vehicles[i].id] = vr;
    }
    setReadings(newReadings);
  };

  // ---- Keyboard navigation ----
  useEffect(() => {
    const handler = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const maxRow = vehicles.length - 1;
      const maxCol = visibleColumns.length - 1;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          focusColRef.current = Math.min(focusColRef.current + 1, maxCol);
          highlightCell();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          focusColRef.current = Math.max(focusColRef.current - 1, 0);
          highlightCell();
          break;
        case 'ArrowDown':
          e.preventDefault();
          focusRowRef.current = Math.min(focusRowRef.current + 1, maxRow);
          highlightCell();
          break;
        case 'ArrowUp':
          e.preventDefault();
          focusRowRef.current = Math.max(focusRowRef.current - 1, 0);
          highlightCell();
          break;
        case '1': case '2': case '3': case '4': {
          e.preventDefault();
          const v = vehicles[focusRowRef.current];
          const col = visibleColumns[focusColRef.current];
          if (v && col && columnApplies(col, v)) {
            onReadingChange(v.id, col.key, parseInt(e.key) - 1);
          }
          break;
        }
        case '0': case 'Delete': case 'Backspace': {
          e.preventDefault();
          const v2 = vehicles[focusRowRef.current];
          const col2 = visibleColumns[focusColRef.current];
          if (v2 && col2) {
            onReadingChange(v2.id, col2.key, null);
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [vehicles, visibleColumns, onReadingChange]);

  const highlightCell = () => {
    if (!gridRef.current) return;
    // Remove previous highlight
    gridRef.current.querySelectorAll('.inspection-cell-focus').forEach(el => {
      el.classList.remove('inspection-cell-focus', 'ring-2', 'ring-blue-400');
    });
    // Add highlight
    const cell = gridRef.current.querySelector(
      `[data-row="${focusRowRef.current}"][data-col="${focusColRef.current}"]`
    );
    if (cell) {
      cell.classList.add('inspection-cell-focus', 'ring-2', 'ring-blue-400');
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Tab bar + shortcuts */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setCurrentTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                currentTab === tab.key
                  ? 'bg-white text-blue-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSetAllGreen}
            className="px-3 py-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100"
          >
            Tout vert
          </button>
          <button
            onClick={handleCopyFirstRow}
            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
            disabled={vehicles.length < 2}
          >
            Copier 1ere ligne
          </button>
        </div>
      </div>

      {/* Keyboard hint */}
      <p className="text-xs text-gray-400">
        Clavier: fleches pour naviguer, 1-4 pour noter (1=critique, 4=bon), 0 pour effacer
      </p>

      {/* Grid table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg" ref={gridRef}>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 sticky left-0 bg-gray-50 z-10 min-w-[160px]">
                Vehicule
              </th>
              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 min-w-[70px]">
                km
              </th>
              {visibleColumns.map(col => (
                <th
                  key={col.key}
                  className="px-1 py-2 text-center text-xs font-medium text-gray-500 min-w-[90px]"
                  title={col.label}
                >
                  {col.shortLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {vehicles.map((v, rowIdx) => {
              const vr = readings[v.id] || {};
              return (
                <tr key={v.id} className="hover:bg-blue-50/30">
                  {/* Vehicle info */}
                  <td className="px-3 py-2 sticky left-0 bg-white z-10">
                    <div className="font-mono text-xs font-bold text-gray-900">{v.license_plate}</div>
                    <div className="text-xs text-gray-400 truncate">{v.make} {v.model}</div>
                  </td>
                  {/* Odometer */}
                  <td className="px-1 py-1 text-center">
                    {(() => {
                      const currentMileage = v.mileage || 0;
                      const enteredKm = odometers[v.id];
                      const isDecrease = enteredKm != null && currentMileage > 0 && enteredKm < currentMileage;
                      return (
                        <div className="relative">
                          <input
                            type="number"
                            value={enteredKm ?? ''}
                            onChange={(e) => onOdometerChange(v.id, e.target.value ? parseInt(e.target.value) : null)}
                            placeholder={currentMileage > 0 ? `${currentMileage}` : 'km'}
                            min={currentMileage || 0}
                            className={`w-20 px-1 py-1 text-xs text-center border rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400 ${
                              isDecrease ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200'
                            }`}
                            title={currentMileage > 0 ? `Actuel: ${fmt.number(currentMileage)} km` : ''}
                          />
                          {isDecrease && (
                            <div className="absolute -bottom-3 left-0 right-0 text-[8px] text-red-500 font-medium whitespace-nowrap">
                              min {fmt.number(currentMileage)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* Resource cells */}
                  {visibleColumns.map((col, colIdx) => {
                    const applies = columnApplies(col, v);
                    const val = vr[col.key];
                    return (
                      <td
                        key={col.key}
                        data-row={rowIdx}
                        data-col={colIdx}
                        className="px-1 py-1 text-center"
                      >
                        {applies ? (
                          <div className="flex gap-0.5 justify-center">
                            {RANGE_GLYPHS.map((glyph, idx) => (
                              <button
                                key={idx}
                                onClick={() => onReadingChange(v.id, col.key, val === idx ? null : idx)}
                                className={`w-5 h-5 rounded text-[10px] font-bold transition-all leading-none flex items-center justify-center ${
                                  val === idx
                                    ? `${RANGE_COLORS.bg[idx]} text-white shadow-sm`
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                                title={`${col.label}: ${['Critique','Attention','Acceptable','Bon'][idx]}`}
                              >
                                {glyph}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
